import React, { useMemo, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

// ---- Hardcoded sample emails (read-only demo) ----
const sampleEmails = [
  {
    id: "671eb757-a6e1-4c81-ae52-2f258375c4c6",
    subject: "הקלטת ניטוב מטלפון 0533127589 למספר 0534161356",
    from: "yemot@yemot.co.il (ימות המשיח)",
    to: "DCT.0554410703@HAZRAN.ONLINE",
    date: 1755084057, // Wed, 13 Aug 2025 14:20:57 +0300
    snippet:
      'שלום, מצ"ב הקלטת ניטוב. מטלפון: 0533127589, מספר יעד: 0534161356. זמן: 14:20:33.',
    headers: `Content-Type: multipart/mixed; boundary="b1_709c570b5f21642f17a325f0a7d14ce6"
Date: Wed, 13 Aug 2025 14:20:54 +0300
From: ימות המשיח - פתרונות תקשורת <yemot@yemot.co.il>
Subject: הקלטת ניטוב מטלפון 0533127589 למספר 0534161356`,
    text: `ב"ה,  ט אב תשפ"ה 2025-08-13
0534161356 שלום
מצ"ב הקלטת ניטוב...`.trim(),
    attachments: [{ name: "recording.mp3" }],
  },
  {
    id: "0a4de721-1309-4024-b834-3982d6124441",
    subject: "הקלטת ניטוב מטלפון 035060450 למספק 0534161356",
    from: "yemot@yemot.co.il (ימות המשיח)",
    to: "DCT.0554410703@HAZRAN.ONLINE",
    date: 1755076946,
    snippet: 'מצ"ב הקלטת ניטוב. מטלפון: 035060450, יעד: 0534161356. זמן: 12:22:00.',
    headers: `Content-Type: multipart/mixed; boundary="b1_ca4e8cbcc9fac0325c33a6e4b9c56424"
Date: Wed, 13 Aug 2025 12:22:23 +0300
From: ימות המשיח - פתרונות תקשורת <yemot@yemot.co.il>
Subject: הקלטת ניטוב מטלפון 035060450 למספק 0534161356`,
    text: `ב"ה, י אב תשפ"ה 2025-08-13
0534161356 שלום
מצ"ב הקלטת ניטוב...`.trim(),
    attachments: [{ name: "recording.mp3" }],
  },
  {
    id: "76a0aaeb-0eb6-4f8b-b3e2-fa38ed4eeef9",
    subject: "הקלטת ניטוב מטלפון 035060450 למספק 0534161356",
    from: "yemot@yemot.co.il (ימות המשיח)",
    to: "DCT.0554410703@HAZRAN.ONLINE",
    date: 1755076181,
    snippet: 'מצ"ב הקלטת ניטוב. מטלפון: 035060450, יעד: 0534161356. זמן: 12:09:16.',
    headers: `Content-Type: multipart/mixed; boundary="b1_adfbd5dcfea958499f50c46447a349cc"
Date: Wed, 13 Aug 2025 12:09:38 +0300
From: ימות המשיח - פתרונות תקשורת <yemot@yemot.co.il>
Subject: הקלטת ניטוב מטלפון 035060450 למספק 0534161356`,
    text: `ב"ה, ט אב תשפ"ה 2025-08-13
0534161356 שלום
מצ"ב הקלטת ניטוב...`.trim(),
    attachments: [{ name: "recording.mp3" }],
  },
  {
    id: "e6e6690d-1bb8-4bea-beaa-375060e40b95",
    subject: "תורה שבכתב – תחקיר מעמיק: האומנם יש טעות המעתיק ברשי בפרשתנו?",
    from: "0527143909@hazran.online",
    to: "you@example.com",
    date: 1755070000,
    snippet: "הלימוד היומי: פרשת עקב | יום ד| יט מנחם-אב... (תוכן ארוך)",
    headers: `From: תורה שבכתב <0527143909@hazran.online>
Subject: תחקיר מעמיק...
X-Mailer: ...`,
    text: `תוכן ארוך מאוד בעברית (קיצרתי לתצוגת הדמו). אם תרצה אטעין כאן את הטקסט המלא או HTML גולמי.`,
  },
];

// ---- Utils ----
const fmtDate = (epochSeconds) => new Date(epochSeconds * 1000).toLocaleString();

// ---- Read-only Email UI (minimal, CSS-variable theming; inherits site styles) ----
export default function EmailInbox() {
  const [selectedId, setSelectedId] = useState(null);
  const items = sampleEmails;
  const selected = useMemo(() => items.find((e) => e.id === selectedId) || null, [selectedId]);

  return (
    <div className="emailsViewer" dir="rtl">
      <style>{`
        .emailsViewer { color: inherit; font-family: inherit; }
        .emailsViewer .container { max-width: var(--ev-maxw, 980px); margin: 0 auto; padding: var(--ev-pad, 16px); }
        .emailsViewer .card { background: var(--ev-card-bg, #fff); border: 1px solid var(--ev-border, rgba(0,0,0,.12)); border-radius: var(--ev-radius, 12px); box-shadow: var(--ev-shadow, none); overflow: hidden; }
        .emailsViewer .list { list-style: none; margin: 0; padding: 0; }
        .emailsViewer .itemBtn { width: 100%; text-align: right; padding: 12px 16px; background: transparent; border: 0; display: flex; align-items: center; gap: 12px; cursor: pointer; }
        .emailsViewer .itemBtn:hover { background: var(--ev-hover, rgba(0,0,0,.04)); }
        .emailsViewer .subject { font-weight: 600; display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .emailsViewer .metaRow { color: var(--ev-muted, rgba(0,0,0,.6)); font-size: .875rem; display: flex; flex-wrap: wrap; gap: 8px; }
        .emailsViewer .snippet { color: var(--ev-body, inherit); margin-top: 4px; font-size: .95rem; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
        .emailsViewer .backBtn { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 10px; border: 1px solid var(--ev-border, rgba(0,0,0,.12)); background: var(--ev-card-bg, #fff); }
        .emailsViewer .section { border: 1px solid var(--ev-border, rgba(0,0,0,.12)); border-radius: var(--ev-radius, 12px); background: var(--ev-card-bg, #fff); padding: 12px; }
        .emailsViewer pre { margin: 0; white-space: pre-wrap; line-height: 1.7; font-size: .95rem; }
        .emailsViewer .hdrLabel { font-size: .75rem; font-weight: 700; color: var(--ev-muted, rgba(0,0,0,.6)); margin-bottom: 6px; }
        .emailsViewer .headers { background: var(--ev-soft, rgba(0,0,0,.03)); padding: 10px; border-radius: 10px; border: 1px solid var(--ev-border, rgba(0,0,0,.1)); max-height: 260px; overflow: auto; }
        .emailsViewer .attach { display: inline-flex; align-items: center; gap: 8px; padding: 6px 10px; border: 1px solid var(--ev-border, rgba(0,0,0,.12)); border-radius: 999px; background: var(--ev-card-bg, #fff); }
      `}</style>

      <div className="container">
        {!selected && <h1 className="title">תיבת דוא"ל</h1>}

        {/* List view */}
        {!selected && (
          <div className="card">
            <ul className="list">
              {items.map((m) => (
                <li key={m.id}>
                  <button className="itemBtn" onClick={() => setSelectedId(m.id)} title="פתח הודעה">
                    <ChevronLeft size={16} style={{opacity:.6}} />
                    <div style={{flex:1, minWidth:0}}>
                      <div className="subject">{m.subject}</div>
                      <div className="metaRow">
                        <span>• {m.from}</span>
                        <span>{fmtDate(m.date)}</span>
                      </div>
                      {m.snippet && <div className="snippet">{m.snippet}</div>}
                    </div>
                    <ChevronRight size={16} style={{opacity:.6}} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Detail view */}
        {selected && (
          <article className="details" style={{display:"grid", gap:16}}>
            <div style={{display:"flex", justifyContent:"space-between"}}>
              <button className="backBtn" onClick={() => setSelectedId(null)} title="חזרה לכל ההודעות">
                <ChevronRight size={16} /> חזרה
              </button>
            </div>

            <header style={{display:"grid", gap:6}}>
              <h2 style={{fontSize:"1.125rem", fontWeight:700, wordBreak:"break-word"}}>{selected.subject}</h2>
              <div className="metaRow">
                <span style={{wordBreak:"break-word"}}><strong>משלוח:</strong> {selected.from}</span>
                <span style={{wordBreak:"break-word"}}><strong>אל:</strong> {Array.isArray(selected.to) ? selected.to.join(", ") : selected.to}</span>
                <span>{fmtDate(selected.date)}</span>
              </div>
            </header>

            <section className="section">
              {selected.html ? (
                <iframe title="email-html" style={{width:"100%", height:420, background:"#fff", borderRadius:8, border:0}} srcDoc={selected.html} />
              ) : (
                <pre>{selected.text || selected.snippet}</pre>
              )}
            </section>

            {selected.headers && (
              <section>
                <div className="hdrLabel">Headers</div>
                <pre className="headers">{selected.headers}</pre>
              </section>
            )}

            {selected.attachments?.length ? (
              <section style={{display:"flex", flexWrap:"wrap", gap:8}}>
                {selected.attachments.map((a, i) => (
                  <span key={i} className="attach">📎 {a.name}</span>
                ))}
              </section>
            ) : null}
          </article>
        )}
      </div>
    </div>
  );
}
